"use client"

import type React from "react"
import { useState, useMemo, useEffect, useCallback } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { useSchoolFinder } from "../hooks/useSchoolFinder"
import SchoolSearch from "./SchoolSearch"
import SchoolResults from "./SchoolResults"
import { Skeleton } from "./ui/skeleton"
import AuthModal from "@/components/AuthModal"
import type { School } from "@/types"

export type SortOption =
  | "name"
  | "sv3"
  | "sv6"
  | "sv9"
  | "grades9"
  | "information_5"
  | "stimulans_5"
  | "stod_5"
  | "studiero_5"
  | "trygghet_5"
  | "information_8"
  | "stimulans_8"
  | "stod_8"
  | "studiero_8"
  | "trygghet_8"

const extractAreaName = (area: string) => {
  return area.includes("-") ? area.split("-").pop() || area : area
}

export default function SchoolFinder({
  initialAreas = [],
  isAreaPage = false,
  initialSchools = [],
  serializedSearchParams,
  error: initialError,
}: {
  initialAreas?: string[]
  isAreaPage?: boolean
  initialSchools?: School[]
  serializedSearchParams?: string
  error?: string | null
}) {
  const router = useRouter()
  const clientSearchParams = useSearchParams()
  const searchParams = useSearchParams()
  const parsedSearchParams = useMemo(() => {
    if (!serializedSearchParams) return {}
    try {
      return JSON.parse(serializedSearchParams)
    } catch (error) {
      console.error("Error parsing serializedSearchParams:", error)
      return {}
    }
  }, [serializedSearchParams])

  const {
    selectedAreas,
    setSelectedAreas,
    groupedAreas,
    schoolType,
    setSchoolType,
    schoolLevel,
    setSchoolLevel,
    academicPerformance,
    setAcademicPerformance,
    schools,
    inputErrors,
    handleSubmit,
    noResultsMessage,
    area: areaFromHook,
    handleAreaChange,
    handleAddArea,
    handleAddMultipleAreas,
    isLoading,
    setIsLoading,
    resetAllCriteria,
  } = useSchoolFinder(initialAreas, initialSchools, parsedSearchParams)

  const [error, setError] = useState<string | null>(initialError || null)
  const [sortBy, setSortBy] = useState<SortOption>("name")
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  useEffect(() => {
    const areasParam = clientSearchParams.getAll("areas")
    const typeParam = clientSearchParams.get("type")
    const levelParam = clientSearchParams.get("level")
    const performanceParam = clientSearchParams.get("performance")

    const paramAreas =
      areasParam.length > 0 ? areasParam.map((area) => extractAreaName(decodeURIComponent(area.trim()))) : []

    const uniqueAreas = Array.from(new Set([...initialAreas, ...paramAreas]))
    setSelectedAreas(uniqueAreas)

    if (typeParam) {
      setSchoolType(typeParam)
    }

    if (levelParam) {
      setSchoolLevel(levelParam.split(","))
    }

    if (performanceParam) {
      setAcademicPerformance(performanceParam)
    }
  }, [clientSearchParams, initialAreas, setSelectedAreas, setSchoolType, setSchoolLevel, setAcademicPerformance])

  const sortedSchools = useMemo(() => {
    return [...schools].sort((a: School, b: School) => {
      if (sortBy === "name") {
        return a.name.localeCompare(b.name, "sv-SE")
      } else if (sortBy === "grades9") {
        return (Number(b.grades9) || 0) - (Number(a.grades9) || 0)
      } else if (["sv3", "sv6", "sv9"].includes(sortBy)) {
        const aValue = Number(a.testResults[sortBy as keyof typeof a.testResults].score) || 0
        const bValue = Number(b.testResults[sortBy as keyof typeof b.testResults].score) || 0
        return bValue - aValue
      } else {
        // Survey dimensions
        const grade = sortBy.slice(-1)
        const surveyResults = grade === "5" ? "surveyResults5" : "surveyResults8"

        const getDimensionLabel = (sortByValue: string) => {
          const option = sortOptions.find((opt) => opt.value === sortByValue)
          return option ? option.label.split(" (")[0] : ""
        }

        const dimensionLabel = getDimensionLabel(sortBy)

        const aSurveyResult = a[surveyResults as keyof School].find((result) => result.dimension === dimensionLabel)
        const bSurveyResult = b[surveyResults as keyof School].find((result) => result.dimension === dimensionLabel)

        const aValue = aSurveyResult ? aSurveyResult.score : 0
        const bValue = bSurveyResult ? bSurveyResult.score : 0

        return bValue - aValue
      }
    })
  }, [schools, sortBy])

  const capitalizeFirstLetter = (string: string) => {
    return extractAreaName(string).charAt(0).toUpperCase() + extractAreaName(string).slice(1).toLowerCase()
  }

  const handleSchoolFinderSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setIsLoading(true)
      try {
        const result = await handleSubmit(e)
        if (result && result.url) {
          // Always redirect to the general /skola page with query parameters
          const newUrl = `/skola${result.url}`
          await router.push(newUrl, undefined, { shallow: true })
        }
      } catch (error) {
        console.error("Error submitting form:", error)
        setError(error.message || "Ett fel uppstod.") // Set error message
      } finally {
        setIsLoading(false)
      }
    },
    [handleSubmit, router, setIsLoading],
  )

  const handleResetAllCriteria = useCallback(() => {
    resetAllCriteria()
  }, [resetAllCriteria])

  return (
    <div className="w-full max-w-[2000px] mx-auto p-4 bg-white rounded-lg shadow-md">
      {error && <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">{error}</div>}
      <div className="mb-4 text-center">
        <h1 className="text-3xl font-bold mb-2">
          {isAreaPage
            ? `Hitta skolor i ${initialAreas.map(capitalizeFirstLetter).join(", ")}`
            : selectedAreas.length > 0
              ? `Hitta skolor i ${selectedAreas.map(capitalizeFirstLetter).join(", ")}`
              : "Hitta skolor"}
        </h1>
        <p className="text-lg text-gray-600">
          {selectedAreas.length > 0
            ? `Hitta skolorna i ${selectedAreas.map(capitalizeFirstLetter).join(", ")} som är bäst på det du prioriterar!`
            : "Hitta skolorna i ditt område som är bäst på det du prioriterar!"}
        </p>
      </div>
      <SchoolSearch
        selectedAreas={selectedAreas}
        setSelectedAreas={setSelectedAreas}
        area={areaFromHook}
        handleAreaChange={handleAreaChange}
        handleAddArea={handleAddArea}
        handleAddMultipleAreas={handleAddMultipleAreas}
        isDropdownOpen={isDropdownOpen}
        setIsDropdownOpen={setIsDropdownOpen}
        groupedAreas={groupedAreas}
        schoolType={schoolType}
        setSchoolType={setSchoolType}
        schoolLevel={schoolLevel}
        setSchoolLevel={setSchoolLevel}
        academicPerformance={academicPerformance}
        setAcademicPerformance={setAcademicPerformance}
        inputErrors={inputErrors}
        handleSubmit={handleSchoolFinderSubmit}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        resetAllCriteria={handleResetAllCriteria}
        isAreaPage={isAreaPage}
        initialAreas={initialAreas}
      />
      {isLoading ? (
        <Skeleton />
      ) : (
        <SchoolResults
          sortedSchools={sortedSchools}
          noResultsMessage={noResultsMessage}
          setIsAuthModalOpen={setIsAuthModalOpen}
          sortBy={sortBy}
          setSortBy={setSortBy}
          sortOptions={sortOptions}
          searchParams={searchParams}
        />
      )}
      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
    </div>
  )
}

export const sortOptions: { value: SortOption; label: string }[] = [
  { value: "name", label: "Alfabetisk ordning" },
  { value: "sv3", label: "Svenska årskurs 3" },
  { value: "sv6", label: "Svenska årskurs 6" },
  { value: "sv9", label: "Svenska årskurs 9" },
  { value: "grades9", label: "Genomsnittligt meritvärde" },
  { value: "information_5", label: "Information om utbildningen (åk 5)" },
  { value: "stimulans_5", label: "Stimulans (åk 5)" },
  { value: "stod_5", label: "Stöd (åk 5)" },
  { value: "studiero_5", label: "Studiero (åk 5)" },
  { value: "trygghet_5", label: "Trygghet (åk 5)" },
  { value: "information_8", label: "Information om utbildningen (åk 8)" },
  { value: "stimulans_8", label: "Stimulans (åk 8)" },
  { value: "stod_8", label: "Stöd (åk 8)" },
  { value: "studiero_8", label: "Studiero (åk 8)" },
  { value: "trygghet_8", label: "Trygghet (åk 8)" },
]

